import { EventEmitter, Component, OnInit, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { sweetAlert } from 'src/app/utils/swal';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TutorialService } from 'src/app/services/core/tutorials/tutorial.service';
import { DocumentService } from 'src/app/services/core/document/document.service';
import { getFileNameFromUrl } from 'src/app/utils/utils.common';


@UntilDestroy()

@Component({
  selector: 'app-add-tutorial',
  templateUrl: './add-tutorial.component.html',
  styleUrls: ['./add-tutorial.component.css']
})
export class AddTutorialComponent implements OnInit {

  tutorialForm!: FormGroup;
  submitted = false;
  tutorialId: any;
  invalidFileType = false;
  isUploading = false;
  showFileInfo = false;
  selectedFile: File | null = null;
  @Output() onSaveTutorial = new EventEmitter<any>();
  @Output() onCloseModal = new EventEmitter<string>();
  @Input() source: any;
  @Input() tutorials: any;
  selecteType = [];
  types:any;
  typeLabelMap: { [key: string]: string } = {
    'fundingsource': 'Health',
    'provider-fundingsource': 'Fundingsource'
  };


  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private tutorialService: TutorialService,
    private documentService: DocumentService,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.getAllTypes();

    if (this.source === 'modal' && this.tutorials) {
      this.tutorialId = this.tutorials.id || this.tutorials._id;
      this.tutorialForm.patchValue(this.tutorials);
      if (this.tutorials.audience) {
        this.selecteType = this.tutorials.audience;
      }
    } else {
      this.tutorialId = this.route.snapshot.params['id'];
    }

    if (this.tutorialId) {
      this.getTutorialById();
    }
  }

  initForm() {
    this.tutorialForm = this.fb.group({
      name: ['', [Validators.required]],
      description: ['', [Validators.required]],
      type: ['', [Validators.required]],
      thumbnailURL: [''],
      videoURL: ['', [Validators.required]],
      audience: ['',Validators.required],
      isActive: [true]
    });

    this.tutorialForm.get('type')?.valueChanges
    .pipe(untilDestroyed(this))
      .subscribe((value) => {
      if (value !== 'document') {
        this.tutorialForm.get('thumbnailURL')?.setValue('');
        this.tutorialForm.get('thumbnailURL')?.setErrors(null);
        }
      });
  }

  runValidator() {
    const control = this.tutorialForm.get('thumbnailURL');
    const value = control?.value;

    if (!value) {
      control?.setErrors(null);
      return;
    }

    const pattern = /\.(jpg|jpeg|png)(\?.*)?$/i;

    if (!pattern.test(value)) {
      control?.setErrors({ invalidImageUrl: true });
    } else {
      control?.setErrors(null);
    }
  }

  get form() {
    return this.tutorialForm.controls;
  }

  getFileName(url: any): string {
    return getFileNameFromUrl(url);
  }

  toggleFileInfo(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.showFileInfo = !this.showFileInfo;
  }

  onFileSelection(event: any) {
    const file: File = event.target?.files?.[0];

    if (!file) {
      return;
    }

    const allowedMimeTypes = [
      // Excel
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",

      // Word
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",

      // PDF
      "application/pdf",

      // Images
      "image/png",
      "image/jpeg",
      "image/jpg",

      // PowerPoint
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-powerpoint",

      // CSV
      "text/csv",

      // Text
      "text/plain",
    ];

    const allowedExtensions = [
      "xlsx",
      "xls",
      "doc",
      "docx",
      "pdf",
      "png",
      "jpg",
      "jpeg",
      "ppt",
      "pptx",
      "csv",
      "txt",
    ];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    const isValidType =
      allowedMimeTypes.includes(file.type) ||
      (fileExtension && allowedExtensions.includes(fileExtension));

    if (!isValidType) {
      this.selectedFile = null;
      this.invalidFileType = true;
      this.isUploading = false;
      this.tutorialForm.patchValue({
        videoURL: '',
      });
      if (event.target) {
        event.target.value = '';
      }
      sweetAlert(
        "Error",
        "Invalid file type. Please upload a valid document or image.",
        "error",
        "OK",
      );

      return;
    }

    this.selectedFile = file;
    this.invalidFileType = false;
    this.isUploading = true;
    this.tutorialForm.patchValue({
      videoURL: '',
    });

    const formData = new FormData();
    formData.append("file", file);

    this.documentService
      .save(null, formData)
      .pipe(untilDestroyed(this))
      .subscribe(
        (data: any) => {
          this.isUploading = false;
          if (data?.fileUrl) {
            this.tutorialForm.patchValue({
              videoURL: data.fileUrl,
            });
            this.tutorialForm.get('videoURL')?.markAsDirty();
            this.tutorialForm.get('videoURL')?.updateValueAndValidity();
          } else {
            this.selectedFile = null;
            this.tutorialForm.patchValue({
              videoURL: '',
            });
            if (event.target) {
              event.target.value = '';
            }
            sweetAlert("Error", "Failed to upload file. Please try again.", "error", "OK");
          }
        },
        (err: any) => {
          this.isUploading = false;
          this.selectedFile = null;
          this.tutorialForm.patchValue({
            videoURL: '',
          });
          if (event.target) {
            event.target.value = '';
          }
          sweetAlert("Error", err, "error", "OK");
        }
      );
  }

  deleteFile(fileInput: HTMLInputElement | null) {
    this.selectedFile = null;
    this.isUploading = false;
    this.invalidFileType = false;
    this.tutorialForm.patchValue({
      videoURL: '',
    });
    this.tutorialForm.get('videoURL')?.markAsTouched();
    this.tutorialForm.get('videoURL')?.updateValueAndValidity();
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onSave() {
    if (this.isUploading) {
      sweetAlert("Please wait", "File upload is in progress. Please wait until it completes.", "info", "OK");
      return;
    }

    this.submitted = true;

    this.tutorialForm.patchValue({
      audience: this.selecteType,
    });

    if (this.tutorialForm.invalid) {
      return;
    }

    const payload = this.tutorialForm.value;

    this.tutorialService.saveTutorial(this.tutorialId, payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (data: any) => {
          if (data) {
            if (this.source === 'modal') {
              this.onSaveTutorial.emit(data);
            } else {
              this.router.navigateByUrl('academy');
            }
          }
        },
        (err: any) => {
          sweetAlert('Error', err, 'error', 'OK');
        }
      );
  }

  getTutorialById() {
    this.tutorialService.getById(this.tutorialId)
      .pipe(untilDestroyed(this))
      .subscribe(
        (data: any) => {
          if (data) {
            this.tutorialForm.patchValue(data);
            this.selecteType = data.audience;
          }
        },
        (err: any) => {
          sweetAlert('Error', err, 'error', 'OK');
        }
      );
  }

  getAllTypes() {
    this.tutorialService.getTypes().pipe(untilDestroyed(this)).subscribe(
        (data: any) => {
          if (data) {
            this.types = data.map((item: string) => ({
              value: item,
            label: this.typeLabelMap[item] || (item.charAt(0).toUpperCase() + item.slice(1))
            }));
          }
        },
        (err) => {
        const errorMessage = err || 'An unexpected error occurred.';
        sweetAlert('Error', errorMessage, 'warning', 'OK');
      }

      );
  }

}